import React from 'react';

function InvestmentsServices() {
  return (
    <main className="bg-white">
      {/* Hero */}
      <section className="relative h-[56vh] min-h-[420px] overflow-hidden">
        <img src="/images/office-1542590_640.jpg" alt="Services" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/60 to-black/80" />
        <div className="relative z-10 h-full flex items-center">
          <div className="max-w-7xl mx-auto px-6">
            <h1 className="text-white text-4xl sm:text-5xl md:text-6xl font-light tracking-[-0.02em]">Services</h1>
            <p className="mt-4 text-white/90 max-w-2xl text-lg">Distribution networks, financial services, and tech-enabled B2B platforms driving operational efficiency.</p>
          </div>
        </div>
      </section>

      {/* Investment Thesis */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-semibold text-gray-900">Services Investment Thesis</h2>
            <p className="mt-3 text-gray-600 max-w-3xl mx-auto">We invest in services businesses with recurring revenue models, high switching costs, and strong unit economics enhanced by technology.</p>
          </div>
          <div className="grid lg:grid-cols-2 gap-12 items-start mt-10">
            <div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-6">The Services Opportunity</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                The global services economy represents over 70% of GDP in developed markets, yet many sectors remain fragmented, analog, and ripe for consolidation. We target services businesses where technology creates defensibility, improves margins, and enables scalable growth beyond traditional labor constraints.
              </p>
              <p className="text-gray-600 leading-relaxed mb-6">
                Our focus includes distribution networks with physical infrastructure moats, B2B services with embedded software, financial services infrastructure, and professional services marketplaces. We seek businesses with predictable cash flows, high customer lifetime value, and opportunities for operational leverage through technology and process innovation.
              </p>
              <div className="bg-purple-50 border-l-4 border-purple-600 p-5 rounded-r-lg">
                <p className="text-sm text-gray-700 leading-relaxed">
                  <strong className="text-purple-700">Market Size:</strong> B2B services market exceeds $8 trillion globally. Tech-enabled services companies achieve 2-3x higher margins and valuation multiples than traditional peers.
                </p>
              </div>
            </div>
            <div className="relative">
              <img src="/images/business-people-working-as-team-conference-room-plans-future_482257-25781.jpg_nur6h3.jpg" alt="Services thesis" className="w-full h-96 object-cover rounded-2xl shadow-xl" />
              <div className="absolute -bottom-5 -right-5 w-40 h-40 bg-purple-600 opacity-10 rounded-3xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Focus Areas */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-semibold text-gray-900 text-center mb-10">Investment Focus</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {t:'Distribution Networks', d:'Last-mile logistics, wholesale distribution, supply chain services, and fulfillment networks with physical infrastructure and customer density.', img:'/images/business-people-meeting-with-opinion-laptop-ideas-collaboration-project-feedback-with-coaching-teamwork-online-review-research-man-women-with-target-management-modern-office_590464-200806.jpg_vbhl80.jpg'},
              {t:'Financial Services', d:'Payment processing, lending, insurance distribution, wealth management platforms, and embedded finance for B2B verticals.', img:'/images/tablet-business-overlay.jpg'},
              {t:'Tech-Enabled B2B', d:'Staffing platforms, facility management, compliance services, procurement networks, and managed services with software differentiation.', img:'/images/medium-shot-business-team-working_23-2149271692.jpg_nigaw2.jpg'}
            ].map(x => (
              <article key={x.t} className="rounded-2xl overflow-hidden bg-white border border-gray-100 shadow-sm hover:shadow-lg transition-shadow duration-300">
                <img src={x.img} alt={x.t} className="w-full h-48 object-cover" />
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{x.t}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{x.d}</p>
                </div>
              </article>
            ))}
          </div>
          <div className="mt-10 grid md:grid-cols-2 gap-6">
            {[
              {title: 'Professional Services Marketplaces', desc: 'Platforms connecting businesses with specialized talent: consulting, legal, accounting, design, and technical services with quality assurance and workflow tools.'},
              {title: 'Managed Services & Outsourcing', desc: 'IT infrastructure management, cybersecurity services, HR/payroll outsourcing, and business process outsourcing with AI-driven efficiency gains.'}
            ].map(item => (
              <div key={item.title} className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <h4 className="font-semibold text-gray-900 mb-2">{item.title}</h4>
                <p className="text-sm text-gray-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Investment Criteria */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-semibold text-gray-900">What We Look For</h2>
            <p className="mt-3 text-gray-600 max-w-3xl mx-auto">Services businesses with clear paths to technology-driven margin expansion and market consolidation.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-10">
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-6">Key Investment Criteria</h3>
              <div className="space-y-4">
                <div className="border-l-4 border-purple-600 pl-5 bg-purple-50/50 py-3 rounded-r-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Recurring Revenue Model</h4>
                  <p className="text-sm text-gray-600">Subscription, retainer, or consumption-based pricing with 80%+ revenue visibility. High customer retention (90%+ GRR) and expansion opportunities.</p>
                </div>
                <div className="border-l-4 border-gray-400 pl-5 bg-gray-50 py-3 rounded-r-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Technology Differentiation</h4>
                  <p className="text-sm text-gray-600">Proprietary software, data assets, or automation tools that create switching costs and enable margin improvement as the business scales.</p>
                </div>
                <div className="border-l-4 border-purple-600 pl-5 bg-purple-50/50 py-3 rounded-r-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Fragmented Market</h4>
                  <p className="text-sm text-gray-600">Large addressable market with no dominant player. Opportunity for roll-up or organic market share gains through superior service delivery.</p>
                </div>
                <div className="border-l-4 border-gray-400 pl-5 bg-gray-50 py-3 rounded-r-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Strong Unit Economics</h4>
                  <p className="text-sm text-gray-600">Positive contribution margin at the unit level. Clear path to 20%+ EBITDA margins at scale through operational leverage.</p>
                </div>
              </div>
            </div>
            <div className="space-y-6">
              <img src="/images/business-people-hiring-job-offer-with-group-documents-contract-employment-b2b-deal-interview-staff-employees-teamwork-merger-project-human-resources-with-paperwork_590464-342156.jpg_tupxel.jpg" alt="Services investment" className="w-full h-64 object-cover rounded-2xl shadow-lg" />
              <div className="bg-gradient-to-br from-gray-900 to-gray-800 text-white p-6 rounded-xl">
                <h4 className="font-semibold mb-4">Value Creation Playbook</h4>
                <ul className="space-y-2 text-sm text-white/90">
                  <li className="flex gap-2"><span className="text-yellow-400">•</span> Technology roadmap & automation implementation</li>
                  <li className="flex gap-2"><span className="text-yellow-400">•</span> Sales excellence & customer success optimization</li>
                  <li className="flex gap-2"><span className="text-yellow-400">•</span> Add-on acquisitions & market consolidation strategy</li>
                  <li className="flex gap-2"><span className="text-yellow-400">•</span> Operational KPIs & margin improvement initiatives</li>
                  <li className="flex gap-2"><span className="text-yellow-400">•</span> Executive team building & compensation alignment</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Portfolio Metrics */}
      <section className="py-16 bg-gradient-to-br from-purple-900 to-purple-800 text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-semibold">Portfolio Performance</h2>
            <p className="mt-3 text-white/80 max-w-3xl mx-auto">Our services portfolio delivers consistent cash flow, high margins, and attractive risk-adjusted returns.</p>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              {metric: '25%+', label: 'Average EBITDA margins'},
              {metric: '95%', label: 'Customer retention rate'},
              {metric: '3.2x', label: 'Revenue multiple at exit'},
              {metric: '40%', label: 'IRR across realized investments'}
            ].map((item, idx) => (
              <div key={idx} className="text-center bg-white/10 backdrop-blur rounded-xl p-6 border border-white/20">
                <div className="text-4xl font-bold text-yellow-400 mb-2">{item.metric}</div>
                <div className="text-sm text-white/80">{item.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h3 className="text-3xl font-semibold text-gray-900">Explore Services Investments</h3>
          <p className="mt-2 text-gray-600">Partner with us to scale your services business through technology and operational excellence.</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <a href="/apply" className="px-6 py-3 rounded-md bg-gradient-to-r from-[#fbbf24] to-[#f59e0b] text-black font-semibold hover:shadow-lg transition-shadow">Apply now</a>
            <a href="/contact" className="px-6 py-3 rounded-md border-2 border-gray-300 text-gray-700 hover:border-gray-400 transition-colors">Contact us</a>
          </div>
        </div>
      </section>
    </main>
  );
}

export default InvestmentsServices;


